import React, {
  ChangeEvent,
  HTMLAttributes,
  useRef,
  memo,
  ForwardedRef,
  forwardRef,
} from 'react';
import cl from 'classnames';
import { EnterKeyHint } from 'src/interface';
import { goToNextElem } from 'src/helper';
import styles from './searchInputStyle.module.scss';

type InputProps = {
  placeholder?: string;
  value: string;
  setValue(value: string): void;
  enterKeyHint?: EnterKeyHint;
  tabIndex?: number;
  disabled?: boolean | null;
  className?: string;
  size?: 'small' | 'middle' | 'medium';
  autoSize?: boolean;
  onEnter?: () => void;
};

export const SearchInputComponent = (
  {
    placeholder,
    value,
    enterKeyHint = 'search',
    tabIndex,
    disabled = false,
    className,
    setValue,
    size = 'medium',
    autoSize = false,
    onEnter,
    ...otherProps
  }: InputProps & HTMLAttributes<HTMLInputElement>,
  forwardedRef: ForwardedRef<HTMLInputElement | null>,
) => {
  const ref = useRef<HTMLInputElement | null>(null);

  return (
    <div className={cl(styles.container, className)}>
      <div
        className={cl(
          styles.inputContainer,
          disabled && styles.disabled,
          size === 'small' && styles.small,
          size === 'middle' && styles.middle,
          autoSize && styles.autoSize,
        )}
      >
        <input
          ref={(node) => {
            ref.current = node;

            if (!forwardedRef) {
              return;
            }

            if (typeof forwardedRef === 'function') {
              forwardedRef(node);
            } else if (
              typeof forwardedRef === 'object' &&
              Object.keys(forwardedRef).includes('current')
            ) {
              forwardedRef.current = node;
            }
          }}
          type="text"
          inputMode="search"
          className={cl(
            styles.input,
            size === 'small' && styles.small,
            autoSize && styles.autoSize,
          )}
          value={value}
          disabled={Boolean(disabled)}
          placeholder={placeholder}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setValue(event.target.value);
          }}
          autoComplete="new-password"
          enterKeyHint={enterKeyHint}
          tabIndex={tabIndex}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              ref.current?.blur();
              onEnter?.();

              if (enterKeyHint === 'next') {
                goToNextElem(tabIndex);
              }
            }
          }}
          {...otherProps}
        />
        {value.length ? (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={cl(
              styles.icon,
              size === 'small' && styles.small,
              size === 'middle' && styles.middle,
              autoSize && styles.autoSize,
            )}
            onClick={() => {
              setValue('');
            }}
          >
            <g clipPath="url(#clip0_4966_54804)">
              <path
                d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                fill="#9EA6BD"
              />
            </g>
            <defs>
              <clipPath id="clip0_4966_54804">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
        ) : (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={cl(
              styles.icon,
              size === 'small' && styles.small,
              size === 'middle' && styles.middle,
              autoSize && styles.autoSize,
            )}
          >
            <path
              d="M21 21L15.0001 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
              stroke="#9AA7BB"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
    </div>
  );
};

export const SearchInput = forwardRef(SearchInputComponent);

export default memo(SearchInput);
